import { useState } from 'react'
import { Head } from '@inertiajs/react'
import { Card, Input, Button, List, Spin, Tooltip, notification } from 'antd'
import { SendOutlined, PlusOutlined, CheckCircleOutlined } from '@ant-design/icons'
import { useChannel } from '../hooks/useChannel'
import { useAuth } from '../providers/AuthProvider'

interface AiTask {
  description: string
  position: number
}

export default function AiTasker() {
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [tasks, setTasks] = useState<AiTask[]>([])
  const [addedToTodo, setAddedToTodo] = useState(false)
  const { isAuthenticated } = useAuth()
  const [api, contextHolder] = notification.useNotification()

  const channel = useChannel('AiTaskerChannel', {
    received: (data) => {
      setLoading(false)
      if (data.status === 'completed') {
        setTasks(data.tasks)
        setAddedToTodo(false)
      } else if (data.status === 'error') {
        api.error({ message: '处理失败', description: data?.error || '请重试' })
      } else if (data.status === 'success') {
        api.success({ message: '操作成功', description: data.message || '' })
      }
    },
    disconnected: () => setLoading(false),
  })

  const handleSubmit = () => {
    if (!isAuthenticated) {
      api.error({ message: '未登录', description: '请先登录' })
      return
    }
    if (!input.trim()) {
      api.warning({ message: '输入为空', description: '请输入需要转化的任务描述' })
      return
    }
    if (!channel) {
      api.error({ message: '连接未建立', description: '请稍后重试' })
      return
    }
    setLoading(true)
    channel.perform('process_input', { input })
  }

  const handleAddToTodo = () => {
    if (!channel) return
    channel.perform('add_to_todo', { tasks })
    api.success({ message: '添加成功', description: '任务已成功添加到 Task 列表' })
    setAddedToTodo(true)
  }

  return (
    <div className="max-w-3xl mx-auto p-4">
      <Head title="AI Tasker" />
      {contextHolder}
      <Card title="将我的脑子里的想法整理成一份任务清单" className="mb-4">
        <div className="flex flex-col gap-4">
          <Input.TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="请输入你想要完成的任务，例如：明天起来我先写下洗衣服了，然后去图书馆，回来去拿快递"
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
          <div className="flex justify-end">
            <Button type="primary" icon={<SendOutlined />} onClick={handleSubmit} disabled={!isAuthenticated || loading}>
              转化任务
            </Button>
          </div>
        </div>
      </Card>

      <Card
        title="你的任务列表"
        extra={
          <Tooltip title={addedToTodo ? '已添加到Task列表' : tasks.length > 0 ? '添加到Task列表' : '需要先生成任务'}>
            <Button
              type="primary"
              onClick={handleAddToTodo}
              icon={addedToTodo ? <CheckCircleOutlined /> : <PlusOutlined />}
              disabled={tasks.length === 0 || addedToTodo}
              style={addedToTodo ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : {}}
            >
              {addedToTodo ? '添加成功' : '将结果添加到Task'}
            </Button>
          </Tooltip>
        }
      >
        {loading ? (
          <div className="flex justify-center py-8"><Spin tip="正在分析..." /></div>
        ) : (
          <List
            dataSource={tasks}
            renderItem={(task) => (
              <List.Item>
                <div className="flex items-center gap-4">
                  <span className="text-gray-500">#{task.position + 1}</span>
                  <span>{task.description}</span>
                </div>
              </List.Item>
            )}
            locale={{ emptyText: '暂无任务，请在上方输入任务描述' }}
          />
        )}
      </Card>
    </div>
  )
}
